<template>
  <grid-table
    :columns="tableColumns"
    :rows="rows"
    :loading="loading"
    :mob-break="0"
    :tab-break="768"
    tab-align-top
    empty-text="No nominations found."
  >
    <template v-if="$scopedSlots['before-row']" #before-row="{ row }">
      <slot name="before-row" :row="row" />
    </template>

    <template #cell:account="{ row }">
      <small>#{{ row.user.member_number }}</small>
    </template>

    <template #cell:name="{ row }">
      <div>
        <span>{{ row.user.full_name }}</span>
        <small v-if="row.user.deleted" class="text-primary ml-1">(deleted)</small>
        <span class="js-membersCount"> ({{ row.nominations.length }})</span>
        <i
          v-if="row.needs_to_be_processed"
          class="nomination_need_to_be_processed nom-ntbp-icon"
          title="The user has unprocessed nominations after the draft rules has changed."
        />
        <div v-for="m in row.members" :key="m.id" class="member_name">
          {{ m.full_name }}
          <span v-if="m.is_event_member"> (EM)</span>
          <small v-if="m.deleted" class="text-primary ml-1">(deleted)</small>
        </div>
        
      </div>
    </template>

    <template #cell:list="{ row }">
      <nomination-list-select
        :value="row.list"
        size-class="multiselect--sm"
        @input="moveGroup != null && moveGroup(row, $event)"
      />
    </template>

    <template #cell:suburb="{ row }">
      <span>{{ row.user.suburb }}<template v-if="row.user.distance_to_draft != null"> ({{ row.user.distance_to_draft }} km)</template></span>
    </template>

    <template #cell:payment="{ row }">
      <app-multiselect
        v-if="activeList === 'accept' && !eventMode"
        class="multiselect--sm"
        :class="paymentClass(row)"
        :key="`payment-${row.user.id}-${errorKey}`"
        :value="groupPaymentOption(row)"
        :options="paymentStatusOptions"
        label="label"
        track-by="value"
        :searchable="false"
        :allow-empty="false"
        placeholder="-"
        select-label=""
        deselect-label=""
        selected-label=""
        size-class="multiselect--sm"
        @select="$event != null && $event.value === 2 && $emit('open-partial-payment', row)"
        @input="$event != null && $event.value !== 2 && $emit('payment-status-change', row, $event.value)"
      >
        <template #option="{ option }">
          <span
            v-if="option.value === 2"
            :class="paymentOptionClass(option.value)"
            style="display: block; width: 100%;"
            @click="groupPaymentOption(row) != null && groupPaymentOption(row).value === 2 && $emit('open-partial-payment', row)"
          >{{ option.label }}</span>
          <span v-else :class="paymentOptionClass(option.value)">{{ option.label }}</span>
        </template>
      </app-multiselect>
    </template>

    <template #cell:comment="{ row }">
      <app-multiselect
        class="multiselect--sm"
        v-if="activeList === 'accept' && !eventMode"
        :key="`commit-${row.user.id}-${errorKey}`"
        :value="groupCommitOption(row)"
        :options="commitOptionsWithNull"
        label="label"
        track-by="value"
        :searchable="false"
        :allow-empty="false"
        placeholder="Payment type"
        select-label=""
        deselect-label=""
        selected-label=""
        size-class="multiselect--sm"
        @input="$event != null && $emit('commit-change', row, $event.value)"
      />
    </template>

    <template v-if="activeList === 'accept' && !eventMode" #cell:amount="{ row }">
      <strong>{{ formatAmount(unpaidAmount(row)) }}</strong>
    </template>

    <template #cell:actions="{ row }">
      <div class="d-flex align-items-center" style="gap: 15px">
        <i
          v-if="activeList === 'accept'"
          :class="row.invoice_is_sent ? 'invoice-sent-icon' : 'invoice-not-sent-icon'"
          :title="row.invoice_is_sent ? 'Invoice sent.' : 'Invoice not sent.'"
        />
        <a
          class="icon-btn icon-btn--edit"
          title="View/edit nominations"
          @click="$emit('open-detail', row)"
        />
        <f-checkbox
          v-if="selectable"
          :id="`row-check-${row.user.id}`"
          label=""
          :value="selectedIds.indexOf(row.user.id) !== -1"
          class="mb-0"
          @input="$emit('toggle-select', row.user.id)"
        />
      </div>
    </template>
  </grid-table>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api'
import GridTable from '@/components/GridTable'
import FCheckbox from '@/components/FormFields/FCheckbox'
import AppMultiselect from '@/components/AppMultiselect'
import NominationListSelect from './NominationListSelect'

const COLUMNS = [
  { key: 'account', label: 'Account', width: '1fr', tabHideLabel: true },
  { key: 'name', label: 'Member Name', width: '2fr', primary: true, tabHideLabel: true },
  { key: 'list', label: 'Nomination Status', width: '2fr', tabWidth: '3fr', tabLabel: true, tabHideLabel: true },
  { key: 'suburb', label: 'Suburb', width: '1fr', tabRow: 2, tabletWidth: '50%', tabLabel: true },
  { key: 'payment', label: 'Payment Status', width: '2fr', tabRow: 2, tabletWidth: '50%', tabLabel: true },
  { key: 'comment', label: 'Payment type', width: '2fr', tabRow: 3, tabletWidth: '67%', tabLabel: true },
  { key: 'actions', label: 'Actions', width: '2fr', tabRow: 3, tabletWidth: '33%', tabLabel: true }
]

function paymentLabel(group) {
  const noms = group.nominations
  if (!noms.length) return '—'
  const paidCount = noms.filter(n => n.is_paid).length
  if (paidCount === noms.length) return 'Paid'
  if (paidCount === 0) return 'Unpaid'
  return 'Partially Paid'
}

function paymentClass(group) {
  const noms = group.nominations
  const paidCount = noms.filter(n => n.is_paid).length
  return paidCount === noms.length ? 'green_text' : 'red_text'
}

function paymentOptionClass(value) {
  return value === 1 ? 'green_text' : 'red_text'
}

function isPartiallyPaid(group) {
  const noms = group.nominations
  if (!noms.length) return false
  const paidCount = noms.filter(n => n.is_paid).length
  return paidCount > 0 && paidCount < noms.length
}

export default defineComponent({
  name: 'NominationsDashboardTable',
  components: { GridTable, NominationListSelect, FCheckbox, AppMultiselect },
  props: {
    nominations: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    draftId: { type: [String, Number], required: true },
    moveGroup: { type: Function, default: null },
    commitOptions: { type: Array, default: () => [] },
    paymentStatusOptions: { type: Array, default: () => [] },
    errorKey: { type: Number, default: 0 },
    selectable: { type: Boolean, default: false },
    selectedIds: { type: Array, default: () => [] },
    activeList: { type: String, default: null },
    eventMode: { type: Boolean, default: false }
  },
  emits: ['toggle-select', 'open-detail', 'commit-change', 'payment-status-change', 'open-partial-payment'],
  setup(props) {
    const rows = computed(() => props.nominations.map(g => (g.id != null ? g : { ...g, id: g.user.id })))

    const AMOUNT_COLUMN = { key: 'amount', label: 'Amount', width: '1fr', tabRow: 3, tabletWidth: '33%', tabLabel: true }

    const tableColumns = computed(() => {
      if (props.activeList === 'accept' && !props.eventMode) {
        const actionsCol = { ...COLUMNS[COLUMNS.length - 1], tabRow: 4, tabletWidth: '100%' }
        return [...COLUMNS.slice(0, -1), AMOUNT_COLUMN, actionsCol]
      }
      return COLUMNS
    })

    function unpaidAmount(group) {
      return group.nominations
        .filter(n => !n.is_paid)
        .reduce((sum, n) => sum + (n.event != null ? Number(n.event.cost) : 0), 0)
    }

    function formatAmount(val) {
      return val === 0 ? '0' : val.toFixed(2)
    }

    const commitOptionsWithNull = computed(() => [{ value: null, label: 'Payment type' }].concat(props.commitOptions))

    function groupCommitOption(group) {
      // Ignore nominations without a commit yet (e.g. just added to an otherwise-set group) —
      // matches monolith's COUNT(DISTINCT Nomination.commit), which SQL excludes NULLs from,
      // so an unset commit on one nomination doesn't blank out an already-agreed payment type.
      const commits = group.nominations.map(n => n.commit).filter(c => c != null)
      if (commits.length === 0) return commitOptionsWithNull.value[0]
      const unique = commits.filter((v, i, a) => a.indexOf(v) === i)
      if (unique.length !== 1) return commitOptionsWithNull.value[0]
      return commitOptionsWithNull.value.find(o => o.value === unique[0]) || commitOptionsWithNull.value[0]
    }

    function groupPaymentOption(group) {
      const values = group.nominations.map(n => n.is_paid)
      if (values.length === 0) return null
      const unique = values.filter((v, i, a) => a.indexOf(v) === i)
      if (unique.length !== 1) return props.paymentStatusOptions.find(o => o.value === 2) || null
      return props.paymentStatusOptions.find(o => o.value === unique[0]) || null
    }

    return { rows, tableColumns, paymentLabel, paymentClass, paymentOptionClass, isPartiallyPaid, unpaidAmount, formatAmount, commitOptionsWithNull, groupCommitOption, groupPaymentOption }
  }
})
</script>

<style scoped>
.member_name {
  font-size: 0.85em;
  color: #888;
}

.nom-ntbp-icon {
  display: inline-block;
  vertical-align: middle;
  margin-left: 6px;
  position: relative;
  top: 2px;
}

.invoice-sent-icon,
.invoice-not-sent-icon {
  display: inline-block;
  background-repeat: no-repeat;
  cursor: default;
}

.invoice-sent-icon {
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='32' height='17' viewBox='0 0 32 17' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M31.4415 16.2083C31.4535 16.2059 31.4652 16.2027 31.4771 16.1996C31.4872 16.197 31.4974 16.1949 31.5073 16.1919C31.5188 16.1883 31.5301 16.184 31.5414 16.1799C31.5514 16.1763 31.5615 16.1731 31.5714 16.1689C31.582 16.1645 31.5923 16.1593 31.6026 16.1544C31.6127 16.1496 31.623 16.1451 31.6329 16.1398C31.6428 16.1345 31.6522 16.1285 31.6618 16.1227C31.6717 16.1168 31.6817 16.1113 31.6913 16.1048C31.7016 16.0979 31.7111 16.0903 31.721 16.0829C31.7293 16.0767 31.7378 16.0709 31.746 16.0643C31.761 16.052 31.7752 16.0388 31.7891 16.0253C31.7916 16.0229 31.7943 16.0209 31.7968 16.0184C31.7994 16.0158 31.8016 16.0129 31.8041 16.0102C31.8174 15.9965 31.8304 15.9825 31.8426 15.9677C31.8497 15.959 31.8559 15.9499 31.8626 15.9409C31.8694 15.9317 31.8766 15.9227 31.883 15.9131C31.8899 15.9028 31.8959 15.8922 31.9022 15.8816C31.9074 15.8727 31.9131 15.864 31.918 15.8549C31.9237 15.8442 31.9285 15.8332 31.9336 15.8224C31.9382 15.8128 31.943 15.8032 31.9471 15.7933C31.9516 15.7827 31.9551 15.7719 31.959 15.761C31.9628 15.7505 31.9668 15.74 31.9701 15.7293C31.9734 15.7184 31.9758 15.7073 31.9786 15.6962C31.9813 15.6853 31.9844 15.6745 31.9866 15.6635C31.9891 15.6505 31.9907 15.6373 31.9926 15.6242C31.9939 15.6149 31.9956 15.6058 31.9965 15.5964C31.9989 15.5734 32.0001 15.5503 32.0001 15.5272V0.693972C32.0001 0.310622 31.6894 0 31.3061 0H9.44595C9.43984 0 9.43392 0.000739729 9.42781 0.000924788C9.41458 0.00129491 9.40144 0.00185038 9.3883 0.00296073C9.37785 0.0037935 9.36748 0.00499662 9.35712 0.00638457C9.34527 0.00786504 9.33352 0.00962298 9.32177 0.0117512C9.31076 0.0136943 9.29984 0.0160073 9.28902 0.0185056C9.27801 0.0210965 9.26699 0.0238726 9.25608 0.0270186C9.24525 0.0300721 9.23461 0.0334027 9.22406 0.0369188C9.21323 0.04062 9.20241 0.0445068 9.19177 0.0487632C9.18168 0.0527419 9.17178 0.0568127 9.16197 0.0612542C9.15105 0.0661582 9.14023 0.071433 9.1295 0.0768923C9.12034 0.0816113 9.11136 0.0864225 9.10248 0.0915117C9.09174 0.0976186 9.08119 0.104096 9.07074 0.110758C9.06213 0.11631 9.05371 0.122046 9.04539 0.127876C9.03558 0.134815 9.02586 0.142033 9.01633 0.14962C9.00763 0.15656 8.99912 0.163685 8.9907 0.170995C8.98237 0.178305 8.97414 0.185614 8.96609 0.193387C8.95693 0.202177 8.94814 0.211337 8.93944 0.22059C8.93509 0.225217 8.93046 0.229288 8.9263 0.234007C8.9238 0.236783 8.92186 0.239837 8.91945 0.242613C8.91076 0.252699 8.90261 0.263247 8.89447 0.273888C8.88855 0.28166 8.88244 0.289432 8.87689 0.29739C8.86967 0.307753 8.86301 0.318394 8.85635 0.329128C8.85089 0.337918 8.84524 0.346616 8.84025 0.355591C8.83442 0.365954 8.82933 0.376595 8.82405 0.387236C8.81924 0.396952 8.81425 0.406668 8.8099 0.416568C8.80536 0.427024 8.80138 0.43785 8.79741 0.448491C8.79343 0.458947 8.78936 0.46931 8.78593 0.479858C8.78251 0.490499 8.77973 0.501325 8.77677 0.512151C8.77381 0.523255 8.77067 0.534266 8.76817 0.545462C8.76585 0.555918 8.76428 0.566559 8.76243 0.577107C8.76039 0.589136 8.75836 0.601165 8.75688 0.613194C8.75568 0.623187 8.75512 0.633365 8.75438 0.643543C8.75345 0.656497 8.75262 0.669452 8.75244 0.682499C8.75234 0.686292 8.75188 0.689994 8.75188 0.693787V4.97347C8.75188 5.35682 9.0625 5.66744 9.44585 5.66744C9.82921 5.66744 10.1398 5.35682 10.1398 4.97347V2.23505L16.3098 7.69773C16.2776 7.7204 16.2466 7.74547 16.2177 7.77425L10.1397 13.8522V9.29164C10.1397 8.90829 9.82911 8.59767 9.44576 8.59767C9.06241 8.59767 8.75179 8.90829 8.75179 9.29164V10.1396H5.16626C4.78291 10.1396 4.47229 10.4502 4.47229 10.8336C4.47229 11.2169 4.78291 11.5275 5.16626 11.5275H8.75179V15.5277C8.75179 15.911 9.06241 16.2216 9.44576 16.2216H31.3059C31.3288 16.2216 31.3516 16.2204 31.3744 16.2182C31.385 16.2172 31.3953 16.2152 31.4057 16.2137C31.4178 16.212 31.4296 16.2106 31.4415 16.2083ZM30.612 13.8523L24.5339 7.77434C24.53 7.77045 24.5259 7.76722 24.5219 7.76351L29.0913 3.8868C29.3835 3.63882 29.4194 3.20097 29.1714 2.90867C28.9236 2.61655 28.4858 2.58047 28.1933 2.82854L20.3856 9.45274L11.2766 1.38804H30.612V13.8523ZM17.1993 8.7559C17.2488 8.70639 17.289 8.65124 17.3203 8.59258L19.9159 10.8906C20.0471 11.0067 20.2114 11.065 20.3759 11.065C20.5356 11.065 20.6952 11.0101 20.825 10.9002L23.4701 8.65596C23.4942 8.69112 23.5213 8.72471 23.5525 8.7559L29.6305 14.8339H11.1213L17.1993 8.7559Z' fill='%2320A81E'/%3e%3cpath d='M0.693973 7.90357H12.4144C12.7978 7.90357 13.1084 7.59295 13.1084 7.2096C13.1084 6.82625 12.7978 6.51562 12.4144 6.51562H0.693973C0.310622 6.51562 0 6.82625 0 7.2096C0 7.59295 0.310622 7.90357 0.693973 7.90357Z' fill='%2320A81E'/%3e%3cpath d='M1.90851 4.07886H7.17114C7.55449 4.07886 7.86511 3.76824 7.86511 3.38489C7.86511 3.00154 7.55449 2.69092 7.17114 2.69092H1.90851C1.52516 2.69092 1.21454 3.00154 1.21454 3.38489C1.21454 3.76824 1.52516 4.07886 1.90851 4.07886Z' fill='%2320A81E'/%3e%3cpath d='M5.7831 13.0693H0.693973C0.310622 13.0693 0 13.38 0 13.7633C0 14.1467 0.310622 14.4573 0.693973 14.4573H5.7831C6.16645 14.4573 6.47708 14.1467 6.47708 13.7633C6.47708 13.38 6.16645 13.0693 5.7831 13.0693Z' fill='%2320A81E'/%3e%3c/svg%3e");
  width: 30px;
  height: 30px;
  background-size: 30px 30px;
}

.invoice-not-sent-icon {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='27' height='23' viewBox='0 0 27 23' fill='none'%3E%3Cpath d='M3.16406 10.0521V0.743478C3.16406 0.331938 3.52387 0 3.95508 0H26.209C26.6423 0 27 0.333624 27 0.743478V14.1261C27 14.5367 26.6458 14.8696 26.209 14.8696H14.1941C14.2229 15.1138 14.2383 15.3618 14.2383 15.613C14.2383 19.3026 11.0446 22.3044 7.11914 22.3044C3.19365 22.3044 0 19.3026 0 15.613C0 13.298 1.25751 11.2539 3.16406 10.0521ZM6.42342 1.48696L15.0315 7.26606L23.7215 1.48696H6.42342ZM25.418 13.3826V2.18028L15.4862 8.78519C15.3493 8.87624 15.1893 8.92174 15.0293 8.92174C14.8681 8.92174 14.707 8.87555 14.5695 8.78326L4.74609 2.18821V9.30449C5.4887 9.05696 6.28741 8.92174 7.11914 8.92174C10.2129 8.92174 12.8516 10.7864 13.8311 13.3826H25.418ZM7.11914 20.8174C10.1723 20.8174 12.6562 18.4827 12.6562 15.613C12.6562 12.7434 10.1723 10.4087 7.11914 10.4087C4.06598 10.4087 1.58203 12.7434 1.58203 15.613C1.58203 18.4827 4.06598 20.8174 7.11914 20.8174Z' fill='%23D92340'/%3E%3Cpath d='M9.97867 17.6053L5.2861 12.9127C4.96216 12.5888 4.43587 12.5898 4.11066 12.915C3.78544 13.2403 3.78441 13.7665 4.10836 14.0905L8.80093 18.7831C9.12487 19.107 9.65116 19.106 9.97637 18.7808C10.3016 18.4556 10.3026 17.9293 9.97867 17.6053Z' fill='%23D92340'/%3E%3Cpath d='M5.28616 18.784L9.97873 14.0915C10.3027 13.7675 10.3016 13.2412 9.97643 12.916C9.65122 12.5908 9.12493 12.5898 8.80099 12.9137L4.10841 17.6063C3.78447 17.9302 3.7855 18.4565 4.11071 18.7817C4.43593 19.1069 4.96221 19.108 5.28616 18.784Z' fill='%23D92340'/%3E%3C/svg%3E");
  width: 26px;
  height: 26px;
  background-size: 26px 26px;
}

@media (max-width: 848px) {
  >>> .gt__row--head {
    display: none;
  }
}

.multiselect__option--highlight .red_text,
.multiselect__option--highlight .green_text {
  color: #fff;
}

</style>



// WEBPACK FOOTER //
// src/views/Nominations/components/NominationsDashboardTable.vue